import { useQuery } from '@tanstack/react-query';

import { useFeatureFlags } from '@/modules/common/hooks/use-feature-flags';
import { getAvailableSettings } from '@/modules/settings/api/settings.api';

import { SettingType } from '../types/setting-type';

const AVAILABLE_SETTINGS_QUERY_KEY = ['available-settings'];

const applyClientSideSettings = ({
  settings,
  showTimeOffs,
  showNotifications,
}: {
  settings: SettingType[];
  showTimeOffs: boolean;
  showNotifications: boolean;
}): SettingType[] => {
  const visibleSettings = showTimeOffs ? settings : settings.filter(s => s !== SettingType.TimeOff);

  // Push notifications are personal, so they follow the personal-account access
  const hasNotifications = showNotifications && settings.includes(SettingType.ProfileInfo);

  return hasNotifications ? [...visibleSettings, SettingType.Notifications] : visibleSettings;
};

export function useAvailableSettings() {
  const { showTimeOffs, showNotifications } = useFeatureFlags();

  return useQuery({
    queryKey: AVAILABLE_SETTINGS_QUERY_KEY,
    queryFn: getAvailableSettings,
    staleTime: 5 * 60 * 1000,
    // Filtering lives in `select`, not `queryFn`, so it re-evaluates against the
    // latest `showTimeOffs` on every render. The backend feature flags they depend on
    // load asynchronously (BackendFeatureFlagsProvider), so `showTimeOffs` can still be
    // false at the moment this query first fetches; baking the filter into `queryFn`
    // freezes that stale value in the cache for the full 5-minute staleTime.
    select: settings => applyClientSideSettings({ settings, showTimeOffs, showNotifications }),
  });
}
